import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import { useCmsAuth } from "../../context/CmsAuthContext";
import { Field, inputClass } from "../cmsApi.jsx";
import {
  CMS_MODULES,
  additionalModuleIds,
  canManageCmsUsers,
  mergeRoleAndExtras,
  roleDefaultModules,
} from "../../lib/cmsAccess";
import {
  PLATFORM_SYSTEMS,
  systemsForRole,
} from "../../lib/platformSystems";
import { findCmsRole, mergeCmsRoleCatalog, rolesForSelect } from "../../lib/cmsRoles";

function mapCallableError(err) {
  const code = err?.code || "";
  const msg = err?.message || "Operación fallida.";
  if (code.includes("failed-precondition") || /sesión OTP/i.test(msg)) {
    return "Sesión OTP requerida o expirada. Vuelva a verificar el código.";
  }
  return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(.*\)\s*$/, "") || msg;
}

function SystemCheckboxes({ value, onChange, disabled, lockedAll }) {
  if (lockedAll) {
    return (
      <p className="text-xs text-brand-dark/55 border border-brand-soft rounded-xl px-3 py-2.5 bg-brand-soft/40">
        Owner: acceso a <strong>todos</strong> los sistemas (Website, Academia, Medicina).
      </p>
    );
  }
  return (
    <div className="grid sm:grid-cols-3 gap-2">
      {PLATFORM_SYSTEMS.map((s) => (
        <label
          key={s.id}
          className={`flex items-center gap-2 text-sm text-brand-dark/80 border border-brand-soft rounded-xl px-3 py-2 ${
            disabled ? "opacity-70" : "hover:border-brand-primary/40"
          }`}
        >
          <input
            type="checkbox"
            disabled={disabled}
            checked={value.includes(s.id)}
            onChange={(e) => {
              if (e.target.checked) onChange([...new Set([...value, s.id])]);
              else {
                const next = value.filter((id) => id !== s.id);
                onChange(next.length ? next : value);
              }
            }}
          />
          <span className="font-medium">{s.label}</span>
        </label>
      ))}
    </div>
  );
}

function ModuleCheckboxes({ items, value, onChange, disabled, lockedIds }) {
  const list = items || CMS_MODULES;
  const locked = new Set(lockedIds || []);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {list.map((m) => {
        const Icon = m.Icon;
        const boxLocked = disabled || locked.has(m.id);
        return (
          <label
            key={m.id}
            className={`flex items-center gap-2.5 text-sm text-brand-dark/80 border border-brand-soft rounded-xl px-3 py-2 ${
              boxLocked ? "opacity-70" : "hover:border-brand-primary/40"
            }`}
          >
            <input
              type="checkbox"
              disabled={boxLocked}
              checked={value.includes(m.id)}
              onChange={(e) => {
                if (e.target.checked) onChange([...new Set([...value, m.id])]);
                else onChange(value.filter((id) => id !== m.id));
              }}
            />
            {Icon ? (
              <Icon size={16} className="text-brand-primary shrink-0" aria-hidden />
            ) : null}
            <span>{m.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function ModuleAccessEditor({ role, catalog, extras = [], onExtrasChange, disabled, isOwner }) {
  const defaults = roleDefaultModules(role, catalog);
  const extraIds = additionalModuleIds(role, catalog);
  const defaultItems = CMS_MODULES.filter((m) => defaults.includes(m.id));
  const extraItems = CMS_MODULES.filter((m) => extraIds.includes(m.id));
  const effective = mergeRoleAndExtras(role, extras, catalog);

  if (role === "medico") {
    return (
      <p className="text-xs text-brand-dark/55 border border-brand-soft rounded-xl px-3 py-2.5 bg-brand-soft/40">
        Sin módulos del sitio. En Medicina verá laboratorio, historias clínicas e informes
        (referencias y recetas cuando existan como pantallas SIGMO).
      </p>
    );
  }

  if (role === "owner" && isOwner) {
    return (
      <p className="text-xs text-brand-dark/55 border border-brand-soft rounded-xl px-3 py-2.5 bg-brand-soft/40">
        Owner: todos los módulos CMS (no se personaliza por usuario).
      </p>
    );
  }

  if (role === "owner") {
    return (
      <p className="text-xs text-brand-dark/55 border border-brand-soft rounded-xl px-3 py-2.5 bg-brand-soft/40">
        Acceso total (módulos y sistemas fijos).
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
          Incluidos en el rol
        </p>
        <p className="text-[11px] text-brand-dark/45">
          Vienen de la pestaña Roles. Si quita módulos ahí, dejan de aparecer aquí y pasan a
          adicionales.
        </p>
        <ModuleCheckboxes
          items={defaultItems}
          value={effective}
          onChange={() => {}}
          disabled
          lockedIds={defaults}
        />
      </div>
      {extraItems.length ? (
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
            Autorizar adicionales
          </p>
          <p className="text-[11px] text-brand-dark/45">
            {isOwner
              ? "Owner y admin pueden marcar pantallas que el rol ya no trae, solo para esta cuenta."
              : "Puede marcar pantallas que el rol ya no trae, solo para esta cuenta."}
          </p>
          <ModuleCheckboxes
            items={extraItems}
            value={extras}
            disabled={disabled}
            onChange={onExtrasChange}
          />
        </div>
      ) : (
        <p className="text-[11px] text-brand-dark/45">
          Este rol ya incluye todas las pantallas CMS salvo Usuarios (solo administradores).
        </p>
      )}
    </div>
  );
}

function RoleSelect({ catalog, value, onChange, disabled, allowOwner, allowAdmin, className }) {
  const options = rolesForSelect(catalog, {
    allowOwner,
    allowAdmin,
    currentValue: value,
  });
  return (
    <select
      className={className || inputClass}
      value={value}
      disabled={disabled}
      onChange={onChange}
    >
      {options.map((r) => (
        <option key={r.id} value={r.id}>
          {r.label}
        </option>
      ))}
    </select>
  );
}

export default function CmsUsersEditor({ catalog: catalogProp, embedded = false }) {
  const catalog = useMemo(() => mergeCmsRoleCatalog(catalogProp), [catalogProp]);
  const { profile, user } = useCmsAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUid, setBusyUid] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [testingSmtp, setTestingSmtp] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("editor");
  const [moduleExtras, setModuleExtras] = useState([]);
  const [extrasByUid, setExtrasByUid] = useState({});
  const [systems, setSystems] = useState(() => systemsForRole("editor"));
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const isOwner = profile?.role === "owner";
  const canManageUsers = canManageCmsUsers(profile);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const fn = httpsCallable(functions, "listCmsUsers");
      const result = await fn({});
      setItems(result.data?.items || []);
    } catch (err) {
      setItems([]);
      setError(mapCallableError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManageUsers) load();
  }, [canManageUsers, load]);

  const templateSystems = useCallback(
    (r, existing) => {
      if (r === "owner") return systemsForRole("owner");
      const def = findCmsRole(catalog, r);
      if (Array.isArray(existing) && existing.length) return systemsForRole(r, existing);
      if (def?.systems?.length) return [...def.systems];
      return systemsForRole(r);
    },
    [catalog]
  );

  useEffect(() => {
    setModuleExtras([]);
    setSystems(templateSystems(role));
    // Solo al cambiar el rol: no borrar extras si llega el catálogo de Firestore.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- templateSystems al cambiar rol
  }, [role]);

  const onImportAcademyStaff = async () => {
    if (!isOwner) return;
    setImporting(true);
    setError("");
    setMessage("");
    try {
      const fn = httpsCallable(functions, "importAcademyStaffToCms");
      const result = await fn({});
      const d = result.data || {};
      const detail =
        Array.isArray(d.imported) && d.imported.length
          ? ` · ${d.imported
              .slice(0, 8)
              .map((x) => `${x.email} (${x.action}/${x.role})`)
              .join("; ")}${d.imported.length > 8 ? "…" : ""}`
          : "";
      const skips =
        Array.isArray(d.skipReasons) && d.skipReasons.length
          ? ` Omitidos: ${d.skipReasons
              .slice(0, 5)
              .map((x) => `${x.email}: ${x.reason}`)
              .join("; ")}`
          : "";
      setMessage(
        `Importados a CMS (rol se asigna aquí): creados ${d.created || 0}, actualizados ${d.updated || 0}, omitidos ${d.skipped || 0}.${detail}${skips} Nuevas cuentas quedan como editor hasta que cambie el rol.`
      );
      await load();
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setImporting(false);
    }
  };

  if (!canManageUsers) {
    return <Navigate to="/admin" replace />;
  }

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    setMessage("");
    try {
      const fn = httpsCallable(functions, "createCmsUser");
      const extras = additionalModuleIds(role, catalog).filter((id) =>
        moduleExtras.includes(id)
      );
      const result = await fn({
        email,
        password,
        role,
        displayName,
        modules: mergeRoleAndExtras(role, extras, catalog),
        moduleExtras: extras,
        systems,
      });
      const temp = result.data?.tempPassword;
      const mailOk = result.data?.emailSent;
      const tempMailOk = result.data?.tempPasswordEmailSent;
      setMessage(
        [
          result.data?.adoptedExistingAuth
            ? `Cuenta Auth existente vinculada al CMS: ${result.data.email} (${result.data.role}).`
            : `Usuario creado: ${result.data.email} (${result.data.role}).`,
          result.data?.passwordKept
            ? "Se conservó la contraseña que ya tenía el usuario."
            : null,
          mailOk ? "Correo de bienvenida enviado." : "Falló correo de bienvenida (SMTP).",
          tempMailOk
            ? "Correo con contraseña temporal enviado."
            : result.data?.passwordKept
              ? "Sin correo de temporal (clave previa conservada)."
              : "Falló correo de contraseña temporal (SMTP).",
          temp ? `Copia local de la temporal: ${temp}` : "",
          result.data?.mustSetPassword
            ? "Deberá definir una contraseña fuerte tras el primer OTP."
            : "",
        ]
          .filter(Boolean)
          .join(" ")
      );
      setEmail("");
      setPassword("");
      setDisplayName("");
      setRole("editor");
      setModuleExtras([]);
      setSystems(templateSystems("editor"));
      await load();
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setCreating(false);
    }
  };

  const onResendInvite = async (item) => {
    if (
      !window.confirm(
        `¿Reenviar invitación a ${item.email}?\n\nSe generará una NUEVA contraseña temporal y se enviarán dos correos (bienvenida + clave). La clave anterior dejará de servir.`
      )
    ) {
      return;
    }
    setBusyUid(item.uid);
    setError("");
    setMessage("");
    try {
      const fn = httpsCallable(functions, "resendCmsUserInvite");
      const result = await fn({ uid: item.uid });
      const temp = result.data?.tempPassword;
      const mailOk = result.data?.emailSent;
      const tempMailOk = result.data?.tempPasswordEmailSent;
      setMessage(
        [
          `Invitación reenviada a ${result.data?.email || item.email}.`,
          mailOk ? "Bienvenida OK." : "Falló bienvenida (SMTP).",
          tempMailOk ? "Temporal OK." : "Falló temporal (SMTP).",
          temp ? `Copia local de la temporal: ${temp}` : "",
        ]
          .filter(Boolean)
          .join(" ")
      );
      await load();
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setBusyUid("");
    }
  };

  const onTestSmtp = async () => {
    setError("");
    setMessage("");
    setTestingSmtp(true);
    try {
      const fn = httpsCallable(functions, "testCmsInstitutionalMail");
      const result = await fn({});
      setMessage(
        `Prueba SMTP enviada a ${result.data?.to || "su correo"}. Revise bandeja y spam (noreply@agdysa.com).`
      );
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setTestingSmtp(false);
    }
  };

  const onUpdate = async (uid, patch) => {
    setBusyUid(uid);
    setError("");
    setMessage("");
    try {
      const fn = httpsCallable(functions, "updateCmsUser");
      await fn({ uid, ...patch });
      setMessage("Usuario actualizado.");
      await load();
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setBusyUid("");
    }
  };

  const onDelete = async (item) => {
    if (item.uid === user?.uid) {
      setError("No puede eliminarse a sí mismo.");
      return;
    }
    if (!window.confirm(`¿Eliminar permanentemente a ${item.email}?`)) return;
    setBusyUid(item.uid);
    setError("");
    setMessage("");
    try {
      const fn = httpsCallable(functions, "deleteCmsUser");
      await fn({ uid: item.uid });
      setMessage(`Eliminado: ${item.email}`);
      await load();
    } catch (err) {
      setError(mapCallableError(err));
    } finally {
      setBusyUid("");
    }
  };

  const roleHint = useMemo(() => {
    const def = findCmsRole(catalog, role);
    if (role === "owner") {
      return "Acceso total (módulos y sistemas fijos).";
    }
    if (role === "medico") {
      return "Solo Medicina (SIGMO). Sin CMS ni Academia. Jornada 8 h, sin OTP staff.";
    }
    if (def?.label) {
      return isOwner
        ? `Plantilla: ${def.label}. Owner y admin pueden autorizar módulos extra más abajo.`
        : `Plantilla: ${def.label}. Puede autorizar módulos extra más abajo.`;
    }
    return isOwner
      ? "Plantilla del rol seleccionado. Owner y admin pueden autorizar módulos extra."
      : "Plantilla del rol seleccionado. Puede autorizar módulos extra.";
  }, [role, catalog, isOwner]);

  return (
    <div className={embedded ? "space-y-8" : "max-w-3xl space-y-8"}>
      {!embedded ? (
      <div>
        <h2 className="font-display text-3xl font-black">Usuarios CMS</h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          Los roles de plataforma se asignan aquí y se propagan a Academia/Medicina según sistemas.
          El rol <strong>médico</strong> solo entra a SIGMO (sin CMS).
          {isOwner ? " Owner = todos los sistemas." : ""}
        </p>
      </div>
      ) : null}

      <form
        onSubmit={onCreate}
        className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4"
      >
        <h3 className="font-bold text-lg">Crear usuario</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="editor@agdysa.com"
            />
          </Field>
          <Field label="Contraseña temporal (opcional)">
            <input
              className={inputClass}
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Vacío = generar (alta nueva) o conservar (Auth existente)"
            />
            <p className="text-[11px] text-brand-dark/45 mt-1">
              Si el correo ya tiene Auth y deja vacío, se conserva su clave. Si escribe una débil
              (p. ej. 12345678), se asigna y se exigirá cambio fuerte tras el OTP.
            </p>
          </Field>
          <Field label="Nombre (opcional)">
            <input
              className={inputClass}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nombre visible"
            />
          </Field>
          <Field label="Rol">
            <RoleSelect
              catalog={catalog}
              value={role}
              allowOwner={isOwner}
              allowAdmin={canManageUsers}
              onChange={(e) => setRole(e.target.value)}
            />
            <p className="text-[11px] text-brand-dark/45 mt-1">{roleHint}</p>
          </Field>
        </div>
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/55">
            Módulos de acceso (CMS Website)
          </p>
          <ModuleAccessEditor
            role={role}
            catalog={catalog}
            extras={moduleExtras}
            onExtrasChange={setModuleExtras}
            disabled={creating}
            isOwner={isOwner}
          />
        </div>
        <Field label="Sistemas AGDYSA">
            <SystemCheckboxes
              value={systems}
              onChange={setSystems}
              lockedAll={role === "owner" || role === "medico"}
            />
            <p className="text-[11px] text-brand-dark/45 mt-1">
              El rol se propaga solo a los sistemas marcados (misma cuenta Auth / contraseña).
            </p>
          </Field>
        <button
          type="submit"
          disabled={creating}
          className="bg-brand-primary text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest disabled:opacity-60"
        >
          {creating ? "Creando…" : "Crear usuario"}
        </button>
      </form>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-lg">Usuarios ({items.length})</h3>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onTestSmtp}
              disabled={testingSmtp}
              className="text-xs font-bold uppercase tracking-widest text-brand-dark border border-brand-soft rounded-xl px-3 py-2 hover:bg-brand-soft/50 disabled:opacity-60"
              title="Envía un correo de prueba a su propio email (mismo SMTP que OTP e invitaciones)"
            >
              {testingSmtp ? "Probando SMTP…" : "Probar SMTP"}
            </button>
            {isOwner && (
              <button
                type="button"
                onClick={onImportAcademyStaff}
                disabled={importing}
                className="text-xs font-bold uppercase tracking-widest text-brand-dark border border-brand-soft rounded-xl px-3 py-2 hover:bg-brand-soft/50 disabled:opacity-60"
                title="Trae staff de Academia al CMS. El rol owner/admin/editor se asigna aquí, no en Academia."
              >
                {importing ? "Importando…" : "Importar staff Academia"}
              </button>
            )}
            <button
              type="button"
              onClick={load}
              className="text-xs font-bold uppercase tracking-widest text-brand-primary"
            >
              Actualizar
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {message && <p className="text-sm text-brand-primary font-medium">{message}</p>}
        {loading && <p className="text-sm text-brand-dark/50">Cargando…</p>}

        {!loading && items.length === 0 && (
          <p className="text-sm text-brand-dark/50">No hay usuarios en la colección.</p>
        )}

        <ul className="space-y-4">
          {items.map((item) => {
            const busy = busyUid === item.uid;
            const isSelf = item.uid === user?.uid;
            const itemExtras = Object.prototype.hasOwnProperty.call(extrasByUid, item.uid)
              ? extrasByUid[item.uid]
              : Array.isArray(item.moduleExtras)
                ? additionalModuleIds(item.role, catalog).filter((id) =>
                    item.moduleExtras.includes(id)
                  )
                : [];
            const itemSystems = systemsForRole(
              item.role,
              Array.isArray(item.systems) ? item.systems : undefined
            );
            const lockSystems = item.role === "owner" || item.role === "medico";
            const hideCmsModules = item.role === "medico";
            return (
              <li
                key={item.uid}
                className="border border-brand-soft rounded-2xl p-4 space-y-3"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-bold text-brand-dark truncate">{item.email || item.uid}</p>
                    {item.displayName ? (
                      <p className="text-xs text-brand-dark/50">{item.displayName}</p>
                    ) : null}
                    <p className="text-[10px] font-mono text-brand-dark/35 truncate">{item.uid}</p>
                    <p className="text-[10px] text-brand-dark/45 mt-1">
                      Sistemas:{" "}
                      <span className="font-semibold text-brand-primary">
                        {itemSystems
                          .map((id) => PLATFORM_SYSTEMS.find((s) => s.id === id)?.label || id)
                          .join(" · ")}
                      </span>
                    </p>
                    {item.inviteEmailSentAt ? (
                      <p
                        className={`text-[10px] mt-1 ${
                          item.inviteEmailOk && item.tempPasswordEmailOk
                            ? "text-brand-primary"
                            : "text-amber-700"
                        }`}
                      >
                        Último correo:{" "}
                        {new Date(item.inviteEmailSentAt).toLocaleString("es-SV")}
                        {" · "}
                        bienvenida {item.inviteEmailOk ? "OK" : "falló"}
                        {" · "}
                        temporal {item.tempPasswordEmailOk ? "OK" : "falló/no enviado"}
                        {item.inviteEmailError ? ` · ${item.inviteEmailError}` : ""}
                      </p>
                    ) : (
                      <p className="text-[10px] text-brand-dark/40 mt-1">
                        Sin registro de envío de invitación (altas anteriores al tracking).
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <RoleSelect
                      catalog={catalog}
                      className={`${inputClass} !py-2 !px-3 w-auto min-w-[10rem]`}
                      value={item.role}
                      disabled={busy}
                      allowOwner={isOwner}
                      allowAdmin={canManageUsers}
                      onChange={(e) => {
                        const nextRole = e.target.value;
                        const kept = additionalModuleIds(nextRole, catalog).filter((id) =>
                          itemExtras.includes(id)
                        );
                        onUpdate(item.uid, {
                          role: nextRole,
                          modules: mergeRoleAndExtras(nextRole, kept, catalog),
                          moduleExtras: kept,
                          systems: templateSystems(nextRole, itemSystems),
                        });
                      }}
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        onUpdate(item.uid, {
                          status: item.status === "active" ? "inactive" : "active",
                        })
                      }
                      className={`px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border ${
                        item.status === "active"
                          ? "border-brand-primary text-brand-primary"
                          : "border-amber-500 text-amber-700 bg-amber-50"
                      }`}
                    >
                      {item.status === "active" ? "Activo" : "Inactivo"}
                    </button>
                    <button
                      type="button"
                      disabled={busy || item.mustSetPassword}
                      onClick={() => onUpdate(item.uid, { mustSetPassword: true })}
                      className="px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border border-brand-dark/20 text-brand-dark/70 disabled:opacity-40"
                      title="Exigir contraseña fuerte en el próximo OTP"
                    >
                      {item.mustSetPassword ? "Cambio pendiente" : "Exigir clave fuerte"}
                    </button>
                    <button
                      type="button"
                      disabled={busy || isSelf || item.status !== "active"}
                      onClick={() => onResendInvite(item)}
                      className="px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border border-brand-primary/40 text-brand-primary disabled:opacity-40"
                      title="Regenera temporal y reenvía bienvenida + contraseña"
                    >
                      Reenviar correo
                    </button>
                    <button
                      type="button"
                      disabled={busy || isSelf}
                      onClick={() => onDelete(item)}
                      className="px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest text-red-600 disabled:opacity-40"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
                {(item.role !== "owner" || isOwner) && !hideCmsModules && (
                  <ModuleAccessEditor
                    role={item.role}
                    catalog={catalog}
                    extras={itemExtras}
                    disabled={busy}
                    isOwner={isOwner}
                    onExtrasChange={(extras) => {
                      setExtrasByUid((prev) => ({ ...prev, [item.uid]: extras }));
                      onUpdate(item.uid, {
                        modules: mergeRoleAndExtras(item.role, extras, catalog),
                        moduleExtras: extras,
                      });
                    }}
                  />
                )}
                <div className="space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
                      Sistemas AGDYSA
                    </p>
                    <SystemCheckboxes
                      value={itemSystems}
                      disabled={busy}
                      lockedAll={lockSystems}
                      onChange={(next) => onUpdate(item.uid, { systems: next })}
                    />
                    {itemSystems.includes("medicina") ? (
                      <p className="text-[11px] text-brand-dark/45">
                        Medicina abre la puerta a SIGMO. Rol operativo y módulos SIGMO: Medicina →
                        Usuarios y accesos → Personal AGDYSA (no en este CMS).
                      </p>
                    ) : null}
                  </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
